import { useState } from "react";
import { FileText, Upload, Loader2, FileSearch, BrainCircuit, CheckCircle, AlertCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import axios from "axios";

export default function ERPDocuments() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("text");
  const [docType, setDocType] = useState("invoice");
  const [textContent, setTextContent] = useState("");
  const [result, setResult] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [file, setFile] = useState(null);

  const handleTextProcess = async () => {
    if (!textContent.trim()) { toast({ title: "Please enter document content", variant: "destructive" }); return; }
    setProcessing(true);
    setResult(null);
    try {
      const res = await axios.post("/api/erp/process-document", { doc_type: docType, content: textContent });
      setResult(res.data);
      toast({ title: "Document processed successfully" });
    } catch (err) { toast({ title: "Processing failed", variant: "destructive" }); }
    finally { setProcessing(false); }
  };

  const handleFileProcess = async () => {
    if (!file) { toast({ title: "Please select a file", variant: "destructive" }); return; }
    setProcessing(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("doc_type", docType);
      const res = await axios.post("/api/erp/process-document-upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(res.data);
      toast({ title: "Document processed successfully" });
    } catch (err) { toast({ title: "Processing failed", variant: "destructive" }); }
    finally { setProcessing(false); }
  };

  const handleAnomalyDetection = async () => {
    setProcessing(true);
    setResult(null);
    try {
      const res = await axios.post("/api/erp/anomaly-detection", { fiscal_year: new Date().getFullYear(), period: new Date().getMonth() + 1 });
      setResult({ anomalies: res.data.anomalies });
      toast({ title: "Anomaly detection complete" });
    } catch (err) { toast({ title: "Anomaly detection failed", variant: "destructive" }); }
    finally { setProcessing(false); }
  };

  const renderResult = () => {
    if (!result) return null;
    return (
      <Card className="mt-4 border-green-200 bg-green-50">
        <CardHeader><CardTitle className="flex items-center gap-2 text-sm text-green-700"><CheckCircle className="h-4 w-4" /> AI Extraction Result</CardTitle></CardHeader>
        <CardContent><pre className="text-sm whitespace-pre-wrap font-mono bg-white p-4 rounded-md border overflow-x-auto">{JSON.stringify(result, null, 2)}</pre></CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <div><h1 className="text-2xl font-bold">AI Document Processing</h1><p className="text-muted-foreground">Extract structured data from invoices, delivery notes & purchase orders</p></div>
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="text">Text Input</TabsTrigger>
          <TabsTrigger value="file">File Upload</TabsTrigger>
          <TabsTrigger value="anomaly">Anomaly Detection</TabsTrigger>
        </TabsList>

        <TabsContent value="text" className="mt-4">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-indigo-500" /> Paste Document Content</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div><Label>Document Type</Label><Select value={docType} onValueChange={setDocType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="invoice">Invoice</SelectItem><SelectItem value="delivery_note">Delivery Note</SelectItem><SelectItem value="purchase_order">Purchase Order</SelectItem></SelectContent>
              </Select></div>
              <div><Label>Document Text</Label><Textarea value={textContent} onChange={e => setTextContent(e.target.value)} placeholder="Paste the document text content here for AI extraction..." className="min-h-[200px]" /></div>
              <Button onClick={handleTextProcess} disabled={processing || !textContent.trim()}>{processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <BrainCircuit className="h-4 w-4 mr-2" />} Process with AI</Button>
              {renderResult()}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="file" className="mt-4">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Upload className="h-5 w-5 text-indigo-500" /> Upload Document File</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div><Label>Document Type</Label><Select value={docType} onValueChange={setDocType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="invoice">Invoice</SelectItem><SelectItem value="delivery_note">Delivery Note</SelectItem><SelectItem value="purchase_order">Purchase Order</SelectItem></SelectContent>
              </Select></div>
              <div className="border-2 border-dashed rounded-lg p-8 text-center hover:border-indigo-300 transition-colors">
                <FileSearch className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground mb-2">Upload a document file (TXT, PDF, DOCX)</p>
                <input type="file" accept=".txt,.pdf,.docx,.csv,.json" onChange={e => setFile(e.target.files?.[0] || null)} className="block mx-auto text-sm" />
                {file && <p className="text-sm mt-2 text-indigo-600 font-medium">{file.name}</p>}
              </div>
              <Button onClick={handleFileProcess} disabled={processing || !file}>{processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <BrainCircuit className="h-4 w-4 mr-2" />} Process with AI</Button>
              {renderResult()}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="anomaly" className="mt-4">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><AlertCircle className="h-5 w-5 text-red-500" /> GL Posting Anomaly Detection</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">Analyze posted journal entries for the current period to detect unusual patterns, suspicious amounts, and potential errors using AI.</p>
              <Button onClick={handleAnomalyDetection} disabled={processing}>{processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <BrainCircuit className="h-4 w-4 mr-2" />} Run Anomaly Detection</Button>
              {result?.anomalies && (
                <div className="space-y-3 mt-4">
                  <h3 className="font-semibold">Detected Anomalies</h3>
                  {result.anomalies.length === 0 ? <p className="text-sm text-green-600">No anomalies detected</p> : result.anomalies.map((a, i) => (
                    <Card key={i} className="border-red-200 bg-red-50">
                      <CardContent className="p-3">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium">{a.description || a.reason || "Anomaly"}</p>
                            {a.entry_id && <p className="text-xs text-muted-foreground font-mono">Entry: {a.entry_id}</p>}
                            {a.amount && <p className="text-xs text-red-600">Amount: ${a.amount}</p>}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
